import {
  Avatar,
  Button,
  Card,
  CardHeader,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Pagination,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { answerToChartLabel } from '@/patient-app-common/helpers/progress_helper';
import { useArchiveTrackerMutation } from '@/patient-app-common/api/trackerApi';

export function MetricEntryList({ question, survey, surveyEntries }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [archive] = useArchiveTrackerMutation();

  const [editMode, setEditMode] = useState(false);
  const [entries, setEntries] = useState([]);
  const itemsPerPage = 5;
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const entriesForQuestion = [...question.patientEntries];
    _.remove(entriesForQuestion, { skipped: true });
    _.remove(
      entriesForQuestion,
      ({ answer }) => !_.isNumber(answer) && _.isEmpty(answer)
    );
    entriesForQuestion.sort(
      (a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf()
    );

    setEntries(entriesForQuestion);
  }, [question.patientEntries]);

  useEffect(() => {
    const totalPages = Math.ceil(entries.length / itemsPerPage);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [entries]);

  const handleArchive = async (entryId, entryIndex) => {
    try {
      const result = await archive({ id: entryId, archived: 'true' }).unwrap();

      if (result.success) {
        const globalIndex = (currentPage - 1) * itemsPerPage + entryIndex;
        const updatedEntries = [...entries];
        _.pullAt(updatedEntries, globalIndex);
        setEntries(updatedEntries);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const totalPages = Math.ceil(entries.length / itemsPerPage);
  const entriesToShow = entries.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <>
      <ModalHeader>
        <div className="ml-auto pt-8">
          {!editMode ? (
            <Button
              color="primary"
              onPress={() => setEditMode(true)}
              startContent={<Icon icon="mdi:pencil" />}
            >
              {t('common.action.edit')}
            </Button>
          ) : (
            <Button
              color="primary"
              startContent={<Icon icon="mdi:pencil-off" />}
              onPress={() => setEditMode(false)}
              variant="faded"
            >
              {t('common.action.cancel')}
            </Button>
          )}
        </div>
      </ModalHeader>
      <ModalBody>
        {entriesToShow.map(({ answer, date }, i) => {
          const parsedAnswer = answerToChartLabel(question, answer);
          const formattedDate = dayjs(date).format('MMM D h:mm A');
          const entry = _.find(surveyEntries, ['entryDatetime', date]);

          let { result } = entry;
          if (!result) {
            result = Object.values(survey.results)[0];
          }

          const icon = `/icons/${result?.apptext.recIconImage}`;
          const color = result?.apptext.recRecommendationBackgroundColor;

          return (
            <Card
              key={entry.id}
              isPressable
              onPress={() => {
                if (editMode) {
                  handleArchive(entry.id, i);
                } else {
                  navigate(`/progress/surveys/${entry.id}`);
                }
              }}
            >
              <CardHeader className="flex gap-4">
                <Avatar
                  size="md"
                  src={icon}
                  style={{ backgroundColor: color }}
                />
                <div className="flex flex-col">
                  <p className="text-sm">
                    {parsedAnswer} {question.unit}
                  </p>
                  <p className="text-sm">{formattedDate}</p>
                </div>
                {editMode ? (
                  <Icon icon="mdi:trash-can" className="ml-auto text-lg" />
                ) : (
                  <Icon icon="mdi:chevron-right" className="ml-auto" />
                )}
              </CardHeader>
            </Card>
          );
        })}
      </ModalBody>
      <ModalFooter>
        {entries.length > itemsPerPage && (
          <Pagination
            color="primary"
            isCompact
            page={currentPage}
            showControls
            total={totalPages}
            onChange={setCurrentPage}
          />
        )}
      </ModalFooter>
    </>
  );
}
